/**
 * Frontend entry point
 * Observability & Analytics Infrastructure - Task 3.4, 4.5
 * Localization - Task 10: Polish i18n
 *
 * Initializes i18n before React render
 * Initializes Sentry before React render to catch early errors
 * Initializes PostHog after page load to avoid blocking render
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { Button } from "./components/ui/button";
import { ErrorBoundary, initSentry, captureException } from "./services/sentry";
import { initPostHog } from "./services/posthog";
import { getConsent } from "./services/consent/consent";
import i18n from "./locales"; // Initialize i18next before first render
import { installChunkReloadHandler } from "./services/app-update/chunk-reload";
import { captureSignupAttribution } from "./lib/signup-attribution";

// Registered before the first render so a stale lazy chunk requested during
// the initial route load is already covered.
installChunkReloadHandler();

// GTM plan F1: save the campaign parameters the landing passed in this URL
// before any navigation or Firebase redirect drops them. Runs first, and
// never throws (storage may be unavailable in private mode).
captureSignupAttribution(window.localStorage);

// Initialize Sentry before rendering React app
// This ensures all errors are captured, including initialization errors
//
// The root is created once, up front, and reused by both branches below —
// if initSentry()'s .then() callback ever threw, the thrown error would
// propagate to the trailing .catch() (same promise chain), and calling
// createRoot() a second time on the same DOM node there would double-mount
// the app. Hoisting it out makes exactly one mount happen regardless of
// which branch runs.
const root = createRoot(document.getElementById("root")!);

initSentry().then(() => {
  globalThis.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason);
  });
  globalThis.addEventListener("error", (event) => {
    if (event.error) captureException(event.error);
  });
  root.render(
    <StrictMode>
      <ErrorBoundary
        fallback={(fallbackProps) => (
          <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
              <h1 className="text-xl font-semibold text-destructive mb-4">
                {i18n.t("errors:errorBoundary.title")}
              </h1>
              <p className="text-foreground mb-4">
                {i18n.t("errors:errorBoundary.message")}
              </p>
              {import.meta.env.MODE === "development" && (
                <details className="mt-4 text-sm text-muted-foreground">
                  <summary className="cursor-pointer font-medium">
                    {i18n.t("errors:errorBoundary.details")}
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded overflow-auto text-xs">
                    {fallbackProps.error?.toString()}
                    {fallbackProps.componentStack}
                  </pre>
                </details>
              )}
              <div className="mt-6 flex gap-3">
                <Button
                  variant="default"
                  onClick={() => globalThis.location.reload()}
                  className="flex-1"
                  data-test="error-boundary-refresh"
                >
                  {i18n.t("common:buttons.refreshPage")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => fallbackProps.resetError()}
                  className="flex-1"
                  data-test="error-boundary-retry"
                >
                  {i18n.t("common:buttons.retry")}
                </Button>
              </div>
            </div>
          </div>
        )}
        showDialog={false}
      >
        <App />
      </ErrorBoundary>
    </StrictMode>,
  );

  // Initialize API devtools after render (non-blocking) to avoid import-time side effects in tests.
  if (import.meta.env.MODE === "development") {
    import("./services/api/devtools")
      .then(({ initVardicApiDevtools }) => {
        initVardicApiDevtools();
      })
      .catch((error) => {
        console.warn("Devtools init failed:", error);
      });
  }

  // Initialize PostHog after page load (non-blocking)
  // Consent gate: PostHog analytics is non-essential — only init when the user
  // has opted in (ePrivacy/RODO prior consent). Sentry stays always-on.
  if (import.meta.env.VITE_POSTHOG_KEY && getConsent()?.analytics) {
    initPostHog()
      .catch((error) => {
        console.error(
          "PostHog failed to initialize, continuing without analytics:",
          error,
        );
      });
  }
}).catch((error) => {
  globalThis.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason);
  });
  globalThis.addEventListener("error", (event) => {
    if (event.error) captureException(event.error);
  });
  // Graceful degradation: if Sentry init fails, still render the app
  console.error(
    "Sentry failed to initialize, continuing without error tracking:",
    error,
  );

  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // Still try to initialize PostHog even if Sentry fails
  // Consent gate: PostHog analytics is non-essential — only init when the user
  // has opted in (ePrivacy/RODO prior consent). Sentry stays always-on.
  if (import.meta.env.VITE_POSTHOG_KEY && getConsent()?.analytics) {
    initPostHog()
      .catch((error) => {
        console.error(
          "PostHog failed to initialize, continuing without analytics:",
          error,
        );
      });
  }
});
